"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence, useInView, useReducedMotion, animate } from "framer-motion"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Check, ChevronDown, ChevronLeft, ChevronRight, Users, Building2, ShoppingBag,
  Stethoscope, Clock, Lightbulb, Bot, TrendingUp, BarChart3, Database,
  Rocket, CheckCircle2, Target, Phone, Zap, MessageCircle, Brain, Coins, Wallet,
} from "lucide-react"
import { Progress } from "@/components/ui/progress"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog"
import BookingCalendar from "@/components/booking/booking"
import { BookingSubject } from "@/types/calendar"
import type { Dictionary } from "@/types/dictionary"
import type { AiBlueprintInputs, Blueprint } from "@/types/ai-blueprint"
import { COPY, type WizardLang } from "@/components/ai-blueprint/copy"
import WhyPalmidos from "@/components/persona-landing-page/WhyPalmidos"
import { getUTMParamsFromLocalStorage } from "@/lib/utils"
import { useAnalytics } from "@/hooks/useAnalytics"
import { reportGoogleAdsLead } from "@/lib/analytics/google-ads"

const TOTAL_STEPS = 5
const PAIN_MIN_CHARS = 20
const SETUP_MIN_CHARS = 20
// Felt-work: hold the loading sequence long enough that the report never
// appears suspiciously instantly. Generation now runs a deeper model that
// takes ~60-90s after submit in the worst case, so the checklist advances
// slowly (6 steps × 8s ≈ 48s of visible progress) and holds on the last step
// while the model finishes.
const MIN_SUBMIT_DISPLAY_MS = 3800
const LOADING_STEP_MS = 8000
const WHATSAPP_NUMBER = "972545146464"

const STAGGER = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }
const ITEM = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }

// Scroll-reveal preset: every section animates in as it enters the viewport
// (fires immediately for anything already on-screen). `once` so it never
// re-triggers on scroll-up. This is what makes the long report feel alive -
// each block greets you as you scroll, which reads especially well on mobile.
const REVEAL = {
  variants: ITEM,
  initial: "hidden",
  whileInView: "visible",
  viewport: { once: true, amount: 0.2 },
} as const

// Tactile press feedback - subtle on hover, springy on tap (great on touch).
const TAP = { whileHover: { scale: 1.03 }, whileTap: { scale: 0.96 } }

type ToneKey = "neutral" | "red" | "purple" | "green"

// Animated number that counts up from 0 → value the first time it scrolls into
// view. Respects prefers-reduced-motion (snaps to the value instead).
function CountUp({ value, className }: { value: number; className?: string }) {
  const reduce = useReducedMotion()
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.6 })
  const [display, setDisplay] = useState(0)
  useEffect(() => {
    if (!inView) return
    if (reduce) {
      setDisplay(value)
      return
    }
    const controls = animate(0, value, {
      duration: 1.1,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    })
    return () => controls.stop()
  }, [inView, value, reduce])
  return <span ref={ref} className={className}>{display.toLocaleString("en-US")}</span>
}

// Per-tone class recipes. Legibility in print comes from borders + the
// reading-start accent rail + icon tint - never from a dark fill behind light
// text - so the report stays readable when saved as PDF (dark text on white).
const TONES: Record<ToneKey, { card: string; rail: string; chip: string; eyebrow: string; ring: string; glow: string; line: string }> = {
  neutral: {
    card: "border-white/15 bg-white/[0.04]",
    rail: "border-s-white/40",
    chip: "bg-white/10 text-white/70",
    eyebrow: "text-white/55",
    ring: "border-white/30",
    glow: "bg-white/10",
    line: "from-white/30",
  },
  red: {
    card: "border-red-400/30 bg-red-500/[0.05]",
    rail: "border-s-red-400/70",
    chip: "bg-red-500/15 text-red-300",
    eyebrow: "text-red-300",
    ring: "border-red-400/45",
    glow: "bg-red-500/12",
    line: "from-red-400/60",
  },
  purple: {
    card: "border-[#988BFD]/35 bg-[#988BFD]/[0.06]",
    rail: "border-s-[#988BFD]/70",
    chip: "bg-[#988BFD]/15 text-[#988BFD]",
    eyebrow: "text-[#988BFD]",
    ring: "border-[#988BFD]/45",
    glow: "bg-[#988BFD]/15",
    line: "from-[#988BFD]/60",
  },
  green: {
    card: "border-[#24D56D]/40 bg-[#24D56D]/[0.07]",
    rail: "border-s-[#24D56D]/70",
    chip: "bg-[#24D56D]/15 text-[#24D56D]",
    eyebrow: "text-[#24D56D]",
    ring: "border-[#24D56D]/45",
    glow: "bg-[#24D56D]/15",
    line: "from-[#24D56D]/60",
  },
}

// One primitive for every narrative section - icon chip + numbered step +
// title, with an accent rail and optional flourish. Self-reveals on scroll.
// `mode="stat"` centers it for the big-number panels (cost, value).
function SectionShell({
  tone, railTone, mode = "bar", icon, step, title, badge, flourish, children,
}: {
  tone: ToneKey
  railTone?: ToneKey
  mode?: "bar" | "stat"
  icon: React.ReactNode
  step: string
  title: string
  badge?: React.ReactNode
  flourish?: boolean
  children: React.ReactNode
}) {
  const T = TONES[tone]
  const rail = TONES[railTone ?? tone].rail
  const stat = mode === "stat"
  return (
    <motion.div
      {...REVEAL}
      className={`relative overflow-hidden rounded-2xl border ${T.card} border-s-[3px] ${rail} px-6 py-6 md:px-7 md:py-7 ${stat ? "text-center" : ""}`}
    >
      {flourish && (
        <span aria-hidden className={`ab-print-hide pointer-events-none absolute -top-14 end-[-3rem] h-40 w-40 rounded-full ${T.glow} blur-3xl`} />
      )}
      <div className="relative">
        <div className={`mb-2.5 flex items-center gap-3 ${stat ? "justify-center" : ""}`}>
          <motion.span
            initial={{ scale: 0, rotate: -25 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ type: "spring", stiffness: 320, damping: 18 }}
            className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${T.chip} print:border print:border-[#0d0a1f]/30 print:bg-transparent`}
          >
            {icon}
          </motion.span>
          <h2 className="flex items-center gap-2 text-xl font-bold md:text-2xl">
            <span className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-full border text-[10px] font-bold ${T.ring} ${T.eyebrow}`}>{step}</span>
            {title}
          </h2>
          {badge && <span className="ms-auto flex-shrink-0">{badge}</span>}
        </div>
        {children}
      </div>
    </motion.div>
  )
}

// Connector between sections. Hidden in print. Color previews the destination
// section's mood; the chevron gently bobs to pull the eye downward (stilled
// under reduced-motion). The optional caption uses a reading-forward chevron
// (ChevronLeft in RTL, ChevronRight in LTR) - never a hardcoded arrow glyph.
function FlowArrow({
  accent = "neutral", label, dir,
}: {
  accent?: ToneKey
  label?: string
  dir: "rtl" | "ltr"
}) {
  const T = TONES[accent]
  const reduce = useReducedMotion()
  const Caret = dir === "rtl" ? ChevronLeft : ChevronRight
  return (
    <motion.div
      {...REVEAL}
      aria-hidden
      className="ab-print-hide -my-2 flex select-none flex-col items-center gap-1.5"
    >
      <span className={`h-6 w-px bg-gradient-to-b ${T.line} to-transparent`} />
      <span className={`relative grid h-8 w-8 place-items-center rounded-full border bg-[#0d0a1f] ${T.ring} ${T.eyebrow}`}>
        <span aria-hidden className={`absolute inset-0 rounded-full ${T.glow} blur-md`} />
        <motion.span
          animate={reduce ? undefined : { y: [0, 3, 0] }}
          transition={reduce ? undefined : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="relative flex"
        >
          <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
        </motion.span>
      </span>
      {label && (
        <span className="flex items-center gap-1 text-[11px] font-semibold text-white/55">
          <Caret className="h-3 w-3" />
          {label}
        </span>
      )}
    </motion.div>
  )
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

function LoadingScreen({
  dir,
  steps,
  activeStep,
}: {
  dir: "rtl" | "ltr"
  steps: string[]
  activeStep: number
}) {
  return (
    <div dir={dir} className="flex min-h-[60vh] flex-col items-center justify-center gap-8 px-4 pt-12 pb-12 text-white">
      <div className="relative">
        <div className="h-16 w-16 animate-spin rounded-full border-4 border-white/20 border-t-[#24D56D]" />
        <div className="absolute inset-0 animate-ping rounded-full border-2 border-[#24D56D]/40" />
      </div>
      <ul className="w-full max-w-sm space-y-3">
        {steps.map((label, i) => {
          const done = i < activeStep
          const active = i === activeStep
          return (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: dir === "rtl" ? 12 : -12 }}
              animate={{ opacity: done || active ? 1 : 0.4, x: 0 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-3"
            >
              <span
                className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border transition ${
                  done
                    ? "border-[#24D56D] bg-[#24D56D] text-black"
                    : active
                      ? "border-[#24D56D] text-[#24D56D]"
                      : "border-white/25 text-transparent"
                }`}
              >
                {done ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                ) : active ? (
                  <span className="h-2 w-2 animate-pulse rounded-full bg-[#24D56D]" />
                ) : (
                  <span className="h-2 w-2 rounded-full bg-white/20" />
                )}
              </span>
              <span
                className={`text-sm transition md:text-base ${
                  done ? "text-white/55 line-through decoration-white/30" : active ? "font-semibold text-white" : "text-white/40"
                }`}
              >
                {label}
              </span>
            </motion.li>
          )
        })}
      </ul>
    </div>
  )
}

export default function AiBlueprintClient({
  lang,
  calendarDict,
}: {
  lang: WizardLang
  calendarDict?: Partial<Dictionary["calendar"]>
}) {
  const t = COPY[lang]
  const dir = lang === "he" ? "rtl" : "ltr"

  const [step, setStep] = useState(1)
  const [industry, setIndustry] = useState("")
  const [industryOther, setIndustryOther] = useState("")
  const [customerType, setCustomerType] = useState<"B2B" | "B2C" | "Mixed" | "">("")
  const [painText, setPainText] = useState("")
  const [painChips, setPainChips] = useState<string[]>([])
  const [monthlyHours, setMonthlyHours] = useState("")
  const [currentSetup, setCurrentSetup] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [stepIdx, setStepIdx] = useState(0)
  const [blueprint, setBlueprint] = useState<Blueprint | null>(null)
  const [contact, setContact] = useState<{ name: string; email: string; phone: string } | null>(null)
  const [error, setError] = useState(false)
  const [emailState, setEmailState] = useState<"idle" | "sending" | "sent" | "failed">("idle")
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [extraEmails, setExtraEmails] = useState("")
  const [shareState, setShareState] = useState<"idle" | "sending" | "sent" | "error">("idle")
  const bgPromiseRef = useRef<Promise<Blueprint> | null>(null)
  // The email route only sends a blueprint signed by /generate, and the token
  // covers the inputs as they were at generation time, so both are kept.
  const blueprintTokenRef = useRef<string | null>(null)
  const generatedInputsRef = useRef<AiBlueprintInputs | null>(null)
  const emailedRef = useRef(false)

  const { trackEvent, trackWhatsAppClick, identifyLead } = useAnalytics()

  // Wizard funnel: fire one event per step viewed so we can see exactly where
  // prospects drop off (the "leaks") before they ever reach the contact gate.
  useEffect(() => {
    trackEvent("ai_blueprint_step_viewed", {
      step,
      industry: resolvedIndustry || undefined,
      customer_type: customerType || undefined,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  const isIndustryOther = industry === t.otherKey
  const resolvedIndustry = isIndustryOther ? industryOther.trim() : industry

  const showLoader = loading || dialogOpen

  const contactSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(1, t.contactErrors.name),
        email: z.string().email(t.contactErrors.email),
        phone: z.string().min(6, t.contactErrors.phone),
      }),
    [t.contactErrors.name, t.contactErrors.email, t.contactErrors.phone],
  )
  type ContactForm = z.infer<typeof contactSchema>

  const { register, handleSubmit, formState: { errors } } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
  })

  useEffect(() => {
    // The multi-step "machine working" checklist plays only after the prospect
    // submits (loading=true): reset to the first step, then advance one step
    // every LOADING_STEP_MS and hold on the last. While only the contact dialog
    // is open (loader sitting behind a blurred page), the first step stays
    // active so it reads as "already warming up".
    if (!loading) return
    setStepIdx(0)
    const total = t.loadingMessages.length
    const tick = setInterval(
      () => setStepIdx((i) => (i < total - 1 ? i + 1 : i)),
      LOADING_STEP_MS,
    )
    return () => clearInterval(tick)
  }, [loading, t.loadingMessages.length])

  const togglePainChip = (c: string) =>
    setPainChips((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]))

  const composedPainProcess = (() => {
    const parts: string[] = []
    if (painText.trim()) parts.push(painText.trim())
    if (painChips.length) parts.push(`${t.painChipsPrefix}: ${painChips.join(", ")}`)
    return parts.join("\n\n")
  })()

  const buildInputs = () => ({
    industry: resolvedIndustry,
    customerType,
    painProcess: composedPainProcess,
    monthlyHours: monthlyHours || undefined,
    currentSetup: currentSetup.trim() || undefined,
  })

  const startBgGeneration = () => {
    // Kick off the LLM call the moment the contact dialog opens so the blueprint
    // is usually ready by the time the prospect finishes typing. lead is omitted
    // here - the API skips the lead record when lead is undefined; we post it
    // (reliably) after submit.
    if (bgPromiseRef.current) return
    const inputs = buildInputs()
    bgPromiseRef.current = fetch("/api/ai-blueprint/generate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...inputs, lang }),
    }).then(async (res) => {
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error("generate failed")
      blueprintTokenRef.current = json.blueprintToken ?? null
      generatedInputsRef.current = inputs
      return json.blueprint as Blueprint
    })
  }

  // Reliable lead record → fires the Slack lead alert + CRM. keepalive lets the
  // request survive navigation; we await it so failures are at least observable.
  const postLead = (
    contactData: { name: string; email: string; phone: string },
    bp: Blueprint,
  ) => {
    // Attribution was previously missing entirely on Blueprint leads - attach the
    // captured UTM/click-id/referrer so Slack + CRM + PostHog can attribute them.
    const utm = typeof window !== "undefined" ? getUTMParamsFromLocalStorage() : {}

    // Link this PostHog person to the lead and record the flagship conversion.
    identifyLead(contactData.email, {
      name: contactData.name,
      phone: contactData.phone,
      lead_source: "ai_blueprint",
    })
    trackEvent("ai_blueprint_lead_submitted", {
      form_name: "ai_blueprint",
      lang,
      industry: resolvedIndustry || undefined,
      customer_type: customerType || undefined,
      headline: bp.headline,
    })

    return fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        source: "AI Blueprint",
        name: contactData.name,
        email: contactData.email,
        phone: contactData.phone,
        challenge: painText.trim() || painChips.join(", "),
        lang,
        ...utm,
        aiBlueprintInputs: buildInputs(),
        aiBlueprint: bp,
      }),
    })
      .then((res) => {
        // Google Ads lead only once the server accepted it.
        if (res.ok) reportGoogleAdsLead()
        return res
      })
      .catch((err) => console.error("AI Blueprint lead post failed (non-fatal):", err))
  }

  // Auto-send the branded HTML report to the lead + manager right after
  // the result renders. Called from onContactSubmit.
  const sendEmailReport = async (
    contactData: { name: string; email: string; phone: string },
    bp: Blueprint,
  ) => {
    setEmailState("sending")
    try {
      const res = await fetch("/api/ai-blueprint/email-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          blueprint: bp,
          inputs: generatedInputsRef.current ?? buildInputs(),
          blueprintToken: blueprintTokenRef.current,
          lead: contactData,
          lang,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json?.success === false) throw new Error("email send rejected")
      // The endpoint reports leadSent / managerSent separately. We only
      // consider the auto-send "✓ sent to user" if the LEAD copy went out.
      if (json?.leadSent === false) {
        console.error("[ai-blueprint] auto-email rejected by SendGrid (managerSent=", json?.managerSent, ")")
        setEmailState("failed")
        return
      }
      emailedRef.current = true
      setEmailState("sent")
    } catch (err) {
      console.error("[ai-blueprint] auto-email failed (non-fatal):", err)
      setEmailState("failed")
    }
  }

  // Share-by-email dialog: the prospect adds 1+ comma-separated extra emails,
  // we hit the same endpoint with `additionalEmails` so each gets a copy.
  const sendShareEmails = async () => {
    if (!contact || !blueprint) return
    const list = extraEmails
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean)
    if (list.length === 0) {
      setShareDialogOpen(false)
      return
    }
    setShareState("sending")
    try {
      const res = await fetch("/api/ai-blueprint/email-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          blueprint,
          inputs: generatedInputsRef.current ?? buildInputs(),
          blueprintToken: blueprintTokenRef.current,
          lead: contact,
          lang,
          additionalEmails: list,
        }),
      })
      if (!res.ok) throw new Error("share failed")
      const json = await res.json().catch(() => ({}))
      if (json && json.success === false) throw new Error("share failed: endpoint rejected")
      setShareState("sent")
      setExtraEmails("")
    } catch (err) {
      console.error("AI Blueprint share-email failed:", err)
      setShareState("error")
    }
  }

  const onContactSubmit = async (data: ContactForm) => {
    setDialogOpen(false)
    setLoading(true)
    setError(false)
    setContact(data)
    const submitStart = Date.now()
    try {
      if (!bgPromiseRef.current) startBgGeneration()
      const bp = await bgPromiseRef.current!
      // Hold the loading screen so the full "machine working" checklist plays.
      const remaining = MIN_SUBMIT_DISPLAY_MS - (Date.now() - submitStart)
      if (remaining > 0) await new Promise((r) => setTimeout(r, remaining))
      setBlueprint(bp)
      trackEvent("ai_blueprint_generated", {
        lang,
        industry: resolvedIndustry || undefined,
        customer_type: customerType || undefined,
        headline: bp.headline,
      })
      // Reliable side-effects (Slack lead alert via /api/lead + the emailed report).
      postLead(data, bp)
      if (!emailedRef.current) sendEmailReport(data, bp)
    } catch {
      setError(true)
      bgPromiseRef.current = null
      trackEvent("ai_blueprint_failed", { lang, stage: "generate" })
    } finally {
      setLoading(false)
    }
  }

  const openContactDialog = () => {
    startBgGeneration()
    setDialogOpen(true)
    // Reaching the contact gate is the key pre-conversion funnel step.
    trackEvent("ai_blueprint_contact_opened", {
      lang,
      industry: resolvedIndustry || undefined,
      customer_type: customerType || undefined,
    })
  }

  // ── Result ──
  if (blueprint) {
    const totalWeeks = blueprint.agents.reduce((s, a) => s + (a.weeksToReady || 0), 0)
    // Annual hours reclaimed - unified with the cost panel (also in hours/yr)
    // so the prospect sees ONE consistent unit across the report.
    const annualHoursToday = Math.round(blueprint.currentCost.weeklyHoursToday * 52)
    const annualHoursSaved = Math.round((blueprint.roi.monthlyHoursSaved || 0) * 12)
    // Share of THIS workload the agent carries (75–90 typical). Fall back to the
    // hours ratio if the model omitted it, clamped to a sane 50–95 band.
    const shareOfWorkPct = Math.min(
      95,
      Math.max(
        50,
        Math.round(
          blueprint.roi.shareOfWorkPct ||
            (annualHoursToday > 0 ? (annualHoursSaved / annualHoursToday) * 100 : 85),
        ),
      ),
    )
    // Relative cost visual: an AI agent runs at ~1/10 the cost of doing it by hand.
    const AGENT_COST_PCT = 10
    // 24/7 coverage vs a typical staffed work-week (~45h) for the "always-on" bar.
    const ALWAYS_ON_HOURS = 168
    const STAFFED_HOURS = 45
    const agentsCount = blueprint.agents.length
    const agentsGridClass =
      agentsCount === 1
        ? "mx-auto max-w-md"
        : agentsCount === 2
          ? "grid gap-5 sm:grid-cols-2"
          : "grid gap-5 sm:grid-cols-2 lg:grid-cols-3"

    const impactStyles: Record<string, { card: string; pill: string }> = {
      high: {
        card: "border-[#24D56D]/60 bg-gradient-to-br from-[#24D56D]/[0.10] to-transparent shadow-[0_0_40px_rgba(36,213,109,0.12)]",
        pill: "bg-[#24D56D]/20 text-[#24D56D]",
      },
      medium: {
        card: "border-amber-400/45 bg-amber-400/[0.05]",
        pill: "bg-amber-400/20 text-amber-300",
      },
      low: {
        card: "border-white/20 bg-white/[0.04]",
        pill: "bg-white/10 text-white/70",
      },
    }

    const hoursUnit = lang === "he" ? "שעות / שנה" : "hrs / yr"
    const scrollToBooking = () => document.getElementById("book-meeting")?.scrollIntoView({ behavior: "smooth" })

    const waMessage = lang === "he"
      ? `שלום! קיבלתי אפיון AI: "${blueprint.headline}". אשמח להבין איך אפשר לבנות את הסוכן הראשון.`
      : `Hi! I just got an AI Blueprint: "${blueprint.headline}". I'd love to talk about building the first agent.`
    const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessage)}`

    // B2B / B2C / Mixed badge for the customer section.
    const CustomerIcon = customerType === "B2B" ? Building2 : customerType === "B2C" ? ShoppingBag : Users
    const customerLabel = t.customerTypes.find((c) => c.key === customerType)?.label
    const customerBadge = customerLabel ? (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#988BFD]/40 bg-[#988BFD]/10 px-3 py-1 text-xs font-bold text-[#988BFD]">
        <CustomerIcon className="h-3.5 w-3.5" />
        {customerLabel}
      </span>
    ) : null

    // The prospect picked a RANGE for monthly hours; the last option is the
    // "not sure" escape hatch - only surface the chip when they picked a real range.
    const notSureHours = t.monthlyHoursOptions[t.monthlyHoursOptions.length - 1]
    const showRangeChip = !!monthlyHours && monthlyHours !== notSureHours

    return (
      <div dir={dir} className="ab-result-root mx-auto w-full max-w-4xl px-3 pt-24 pb-24 text-white sm:px-4 sm:pt-32 sm:pb-20">
        <style jsx global>{`
          @media print {
            html, body { background: #ffffff !important; }
            body * { visibility: hidden !important; }
            .ab-result-root, .ab-result-root * { visibility: visible !important; }
            .ab-result-root {
              position: absolute !important;
              left: 0; right: 0; top: 0;
              max-width: 100% !important;
              padding: 24px !important;
              color: #0d0a1f !important;
              background: #ffffff !important;
            }
            .ab-result-root h1, .ab-result-root h2, .ab-result-root h3, .ab-result-root p, .ab-result-root li, .ab-result-root span, .ab-result-root div {
              color: #0d0a1f !important;
            }
            /* Force any scroll-reveal element to its final state for print - a
               section that hasn't entered the viewport must still appear. */
            .ab-result-root * {
              opacity: 1 !important;
              transform: none !important;
            }
            .ab-result-root .ab-print-hide { display: none !important; }
            .ab-result-root section#book-meeting { display: none !important; }
          }
          /* Hide the booking-form "message" textarea inside the AI Blueprint
             result page only - the report already captured the pain in detail.
             Targets the FormItem that wraps the textarea. */
          .ab-book-here form textarea {
            display: none !important;
          }
          .ab-book-here form textarea ~ p,
          .ab-book-here form label[for*="message"],
          .ab-book-here form [data-slot="form-message"] {
            display: none !important;
          }
        `}</style>

        {/* Palmidos logo - visible on screen (subtle) and required-looking on the printed/saved PDF */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 flex justify-center md:mb-8"
        >
          <img src="/palmidos-full-logo.svg" alt="Palmidos" className="h-7 md:h-9" />
        </motion.div>

        <div className="space-y-7">
          {/* Hero */}
          <motion.div
            variants={ITEM}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            {contact?.name && (
              <div className="mb-2 text-sm font-semibold uppercase tracking-wider text-[#24D56D] md:text-base">
                {t.result.greeting(contact.name.split(" ")[0] || contact.name)}
              </div>
            )}
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#24D56D]/40 bg-[#24D56D]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#24D56D]">
              {t.result.personalizedFor(resolvedIndustry || (lang === "he" ? "החברה שלכם" : "your business"))}
            </div>
            <h1 className="text-3xl font-extrabold leading-tight md:text-5xl">{blueprint.headline}</h1>
            {blueprint.subheadline && (
              <p className="mx-auto mt-3 max-w-2xl text-base text-white/70 md:text-lg">{blueprint.subheadline}</p>
            )}
          </motion.div>

          {/* 01 · Client / market */}
          <FlowArrow accent="purple" label={t.result.flow.toCustomer} dir={dir} />
          <SectionShell tone="neutral" railTone="purple" step="01" icon={<Users className="h-5 w-5" />} title={t.result.customerTitle} badge={customerBadge}>
            <p className="whitespace-pre-line leading-relaxed text-white/90 md:text-lg">{blueprint.customerContext}</p>
          </SectionShell>

          {/* 02 · Problem / pain (diagnosis) */}
          <FlowArrow accent="red" label={t.result.flow.toProblem} dir={dir} />
          <SectionShell tone="red" step="02" icon={<Stethoscope className="h-5 w-5" />} title={t.result.painTitle}>
            <p className="whitespace-pre-line leading-relaxed text-white/90 md:text-lg">{blueprint.painAnalysis}</p>
          </SectionShell>

          {/* 03 · Cost in hours */}
          <FlowArrow accent="red" dir={dir} />
          <SectionShell tone="red" mode="stat" step="03" icon={<Clock className="h-5 w-5" />} title={t.result.currentCostTitle} flourish>
            <div className="mt-1 text-4xl font-extrabold tabular-nums text-red-300 md:text-6xl">
              <CountUp value={annualHoursToday} />
              <span className="ms-2 text-2xl font-bold md:text-3xl">{hoursUnit}</span>
            </div>
            {showRangeChip && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-red-400/35 bg-red-500/10 px-4 py-1.5 text-sm font-bold text-red-200 print:border-[#0d0a1f]/30 print:bg-transparent">
                <Clock className="h-4 w-4" />
                {monthlyHours} {lang === "he" ? "/ חודש" : "/ mo"}
              </div>
            )}
            <p className="mt-2 text-xs text-white/60">{t.result.currentCostRangeNote}</p>
            <div className="mt-2 text-sm text-white/75">{blueprint.currentCost.calculation}</div>
          </SectionShell>

          {/* 04 · Solution */}
          <FlowArrow accent="purple" label={t.result.flow.toSolution} dir={dir} />
          <SectionShell tone="purple" step="04" icon={<Lightbulb className="h-5 w-5" />} title={t.result.solutionTitle} flourish>
            <p className="whitespace-pre-line leading-relaxed text-white/90 md:text-lg">{blueprint.solutionOverview}</p>
          </SectionShell>

          {/* 05 · Agents */}
          <FlowArrow accent="green" dir={dir} />
          <motion.div {...REVEAL}>
            <div className="mb-4 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
              <h2 className="flex items-center gap-3 text-xl font-bold md:text-2xl">
                <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#24D56D]/15 text-[#24D56D] print:border print:border-[#0d0a1f]/30 print:bg-transparent">
                  <Bot className="h-5 w-5" />
                </span>
                <span className="flex items-center gap-2">
                  <span className="grid h-5 w-5 flex-shrink-0 place-items-center rounded-full border border-[#24D56D]/45 text-[10px] font-bold text-[#24D56D]">05</span>
                  {t.result.agentsTitle}
                </span>
              </h2>
              {totalWeeks > 0 && (
                <div className="inline-flex items-center gap-1.5 rounded-full border border-[#24D56D]/40 bg-[#24D56D]/10 px-3 py-1 text-xs font-bold text-[#24D56D] print:bg-transparent">
                  <Clock className="h-3.5 w-3.5" /> {t.result.totalTimelineLabel(totalWeeks)}
                </div>
              )}
            </div>
            <div className={agentsGridClass}>
              {blueprint.agents.map((a, i) => {
                const s = impactStyles[a.impact] || impactStyles.low
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.45, delay: i * 0.08 }}
                    whileHover={{ y: -5 }}
                    className={`relative flex flex-col rounded-2xl border p-5 pt-7 md:p-6 md:pt-8 ${s.card}`}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true, amount: 0.6 }}
                      transition={{ type: "spring", stiffness: 320, damping: 16, delay: i * 0.08 + 0.1 }}
                      className="absolute -top-4 start-4 flex h-9 w-9 items-center justify-center rounded-full bg-[#24D56D] text-sm font-extrabold text-black shadow-[0_4px_16px_rgba(36,213,109,0.45)] print:border print:border-[#0d0a1f] print:bg-transparent print:text-[#0d0a1f] print:shadow-none"
                      aria-hidden
                    >
                      {i + 1}
                    </motion.div>
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div className="text-xl font-extrabold text-[#24D56D] md:text-2xl">{a.name}</div>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${s.pill} print:bg-transparent`}>{t.impact[a.impact]}</span>
                    </div>
                    <p className="mb-3 text-sm font-medium leading-relaxed text-white md:text-base">{a.whatItDoes}</p>
                    {a.howItWorks && (
                      <div className="mb-4 rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                        <div className="mb-1 text-xs font-bold uppercase tracking-wider text-white/55">{t.result.howItWorksLabel}</div>
                        <p className="text-sm leading-relaxed text-white/80">{a.howItWorks}</p>
                      </div>
                    )}
                    {a.coreValue && (
                      <div className="mb-4 rounded-xl border border-[#24D56D]/30 bg-[#24D56D]/[0.08] p-3.5">
                        <div className="mb-1 text-xs font-bold uppercase tracking-wider text-[#24D56D]">{t.result.coreValueLabel}</div>
                        <p className="text-sm leading-relaxed text-white/90">{a.coreValue}</p>
                      </div>
                    )}
                    {a.bullets && a.bullets.length > 0 && (
                      <ul className="mb-4 space-y-2 text-sm leading-relaxed text-white/85">
                        {a.bullets.map((b, j) => (
                          <li key={j} className="flex gap-2.5">
                            <span className="mt-2 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#24D56D]" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="mb-3 text-xs italic text-white/65">
                      <span className="font-bold not-italic">{t.result.eliminatesLabel}</span> {a.whatItEliminates}
                    </p>
                    <div className="mt-auto flex items-center gap-3 border-t border-white/10 pt-3 text-xs text-white/65">
                      <span>{t.result.connectsTo(a.connectsTo)}</span>
                      {a.weeksToReady > 0 && (
                        <span className="ms-auto inline-flex items-center gap-1 font-bold text-[#24D56D]">
                          <Clock className="h-3.5 w-3.5" /> {t.result.weeksToReadyLabel(a.weeksToReady)}
                        </span>
                      )}
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>

          {/* 06 · Gain / value - qualitative pillars (NOT an hours number; the
              time math lives in the chart). Why an AI agent beats both the manual
              process and a dumb automation: always-on, instant, on-brand, knows
              the business, a fraction of the cost. */}
          <FlowArrow accent="green" dir={dir} />
          <SectionShell tone="green" step="06" icon={<TrendingUp className="h-5 w-5" />} title={t.result.valueTitle} flourish>
            {blueprint.valueNarrative && (
              <p className="mb-6 whitespace-pre-line leading-relaxed text-white/90 md:text-lg">{blueprint.valueNarrative}</p>
            )}
            {(() => {
              const vp = t.result.valuePillars
              const pillarMeta: Record<string, { icon: React.ReactNode; title: string }> = {
                alwaysOn: { icon: <Clock className="h-5 w-5" strokeWidth={2.2} />, title: vp.alwaysOn.title },
                instant: { icon: <Zap className="h-5 w-5" strokeWidth={2.2} />, title: vp.instant.title },
                brandVoice: { icon: <MessageCircle className="h-5 w-5" strokeWidth={2.2} />, title: vp.brandVoice.title },
                knowsBusiness: { icon: <Brain className="h-5 w-5" strokeWidth={2.2} />, title: vp.knowsBusiness.title },
                lowerCost: { icon: <Coins className="h-5 w-5" strokeWidth={2.2} />, title: vp.lowerCost.title },
              }
              const order = ["alwaysOn", "instant", "brandVoice", "knowsBusiness", "lowerCost"]
              const pillars = (blueprint.valuePillars && blueprint.valuePillars.length > 0
                ? [...blueprint.valuePillars].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
                : order.map((key) => ({ key, oneLiner: "" })))
              return (
                <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
                  {pillars.map((p, i) => {
                    const meta = pillarMeta[p.key] || pillarMeta.alwaysOn
                    return (
                      <motion.div
                        key={p.key}
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.4, delay: i * 0.07 }}
                        whileHover={{ y: -4 }}
                        className="flex flex-col gap-2.5 rounded-xl border border-[#24D56D]/25 bg-[#24D56D]/[0.05] p-4 print:border-[#0d0a1f]/25 print:bg-transparent"
                      >
                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#24D56D]/15 text-[#24D56D] print:border print:border-[#0d0a1f]/30 print:bg-transparent">
                          {meta.icon}
                        </span>
                        <div className="text-base font-extrabold text-white">{meta.title}</div>
                        {p.oneLiner && (
                          <p className="text-sm leading-relaxed text-white/75">{p.oneLiner}</p>
                        )}
                      </motion.div>
                    )
                  })}
                </div>
              )
            })()}
          </SectionShell>

          {/* 07 · Cost / time visuals - three honest comparisons:
              (A) relative cost (manual vs ~1/10 with an agent, no invented ₪),
              (B) share of the work the agent carries + hours back,
              (C) 24/7 coverage vs a staffed week. */}
          <FlowArrow accent="neutral" dir={dir} />
          <SectionShell tone="neutral" step="07" icon={<BarChart3 className="h-5 w-5" />} title={t.result.chartTitle}>
            <div className="grid gap-4 md:grid-cols-3">
              {/* A - relative cost */}
              <div className="flex flex-col rounded-xl border border-white/12 bg-white/[0.03] p-4 print:border-[#0d0a1f]/20 print:bg-transparent">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white/85">
                  <Wallet className="h-4 w-4 text-[#988BFD]" /> {t.result.charts.costTitle}
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="mb-1 flex items-baseline justify-between text-xs">
                      <span className="font-bold text-red-300">{t.result.charts.costToday}</span>
                      <span className="font-extrabold text-red-300">100%</span>
                    </div>
                    <div className="h-4 w-full overflow-hidden rounded-full bg-white/10 print:border print:border-[#0d0a1f]/20">
                      <motion.div className="h-full rounded-full bg-gradient-to-r from-red-500 to-red-400" initial={{ width: 0 }} whileInView={{ width: "100%" }} viewport={{ once: true }} transition={{ duration: 0.9, ease: "easeOut" }} />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex items-baseline justify-between text-xs">
                      <span className="font-bold text-[#24D56D]">{t.result.charts.costAgent}</span>
                      <span className="font-extrabold text-[#24D56D]">~{AGENT_COST_PCT}%</span>
                    </div>
                    <div className="h-4 w-full overflow-hidden rounded-full bg-white/10 print:border print:border-[#0d0a1f]/20">
                      <motion.div className="h-full rounded-full bg-gradient-to-r from-[#24D56D] to-[#24D56D]/70" initial={{ width: 0 }} whileInView={{ width: `${AGENT_COST_PCT}%` }} viewport={{ once: true }} transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }} />
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-white/60">{t.result.charts.costCaption}</p>
              </div>

              {/* B - share of work moved to the agent */}
              <div className="flex flex-col rounded-xl border border-[#24D56D]/30 bg-[#24D56D]/[0.06] p-4 print:border-[#0d0a1f]/20 print:bg-transparent">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white/85">
                  <Bot className="h-4 w-4 text-[#24D56D]" /> {t.result.charts.timeTitle}
                </div>
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                  <div className="text-5xl font-extrabold tabular-nums text-[#24D56D] md:text-6xl">
                    <CountUp value={shareOfWorkPct} />%
                  </div>
                  <div className="mt-1 text-xs text-white/70">{t.result.charts.timeHandledLabel}</div>
                  <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-white/10 print:border print:border-[#0d0a1f]/20">
                    <motion.div className="h-full rounded-full bg-gradient-to-r from-[#24D56D] to-[#24D56D]/70" initial={{ width: 0 }} whileInView={{ width: `${shareOfWorkPct}%` }} viewport={{ once: true }} transition={{ duration: 0.9, ease: "easeOut" }} />
                  </div>
                  {annualHoursSaved > 0 && (
                    <div className="mt-2.5 text-xs font-semibold text-[#24D56D]">{t.result.charts.timeBackLabel(annualHoursSaved)}</div>
                  )}
                </div>
                <p className="mt-3 text-xs leading-relaxed text-white/60">{t.result.charts.timeCaption}</p>
              </div>

              {/* C - 24/7 coverage */}
              <div className="flex flex-col rounded-xl border border-[#988BFD]/30 bg-[#988BFD]/[0.06] p-4 print:border-[#0d0a1f]/20 print:bg-transparent">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white/85">
                  <Clock className="h-4 w-4 text-[#988BFD]" /> {t.result.charts.hoursTitle}
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="mb-1 flex items-baseline justify-between text-xs">
                      <span className="font-bold text-[#988BFD]">{t.result.charts.hoursAlwaysOnLabel}</span>
                      <span className="font-extrabold text-[#988BFD]">{ALWAYS_ON_HOURS}h</span>
                    </div>
                    <div className="h-4 w-full overflow-hidden rounded-full bg-white/10 print:border print:border-[#0d0a1f]/20">
                      <motion.div className="h-full rounded-full bg-gradient-to-r from-[#988BFD] to-[#988BFD]/70" initial={{ width: 0 }} whileInView={{ width: "100%" }} viewport={{ once: true }} transition={{ duration: 0.9, ease: "easeOut" }} />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex items-baseline justify-between text-xs">
                      <span className="font-bold text-white/70">{t.result.charts.hoursStaffedLabel}</span>
                      <span className="font-extrabold text-white/70">{STAFFED_HOURS}h</span>
                    </div>
                    <div className="h-4 w-full overflow-hidden rounded-full bg-white/10 print:border print:border-[#0d0a1f]/20">
                      <motion.div className="h-full rounded-full bg-white/35" initial={{ width: 0 }} whileInView={{ width: `${Math.round((STAFFED_HOURS / ALWAYS_ON_HOURS) * 100)}%` }} viewport={{ once: true }} transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }} />
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-white/60">{t.result.charts.hoursCaption}</p>
              </div>
            </div>
          </SectionShell>

          {/* 08 · Success metrics */}
          {blueprint.successMetrics && blueprint.successMetrics.length > 0 && (
            <>
              <FlowArrow accent="green" dir={dir} />
              <SectionShell tone="green" railTone="green" step="08" icon={<Target className="h-5 w-5" />} title={t.result.successMetricsTitle}>
                <p className="mb-5 max-w-2xl text-sm leading-relaxed text-white/70 md:text-base">
                  {t.result.successMetricsSubtitle}
                </p>
                {/* ROI strip - the two headline numbers up front, so the targets
                    below read against a concrete return. */}
                <div className="mb-5 grid gap-3.5 sm:grid-cols-2">
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.45 }}
                    className="flex items-center gap-4 rounded-xl border border-[#24D56D]/35 bg-[#24D56D]/[0.07] p-4 print:border-[#0d0a1f]/25 print:bg-transparent"
                  >
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#24D56D]/15 text-[#24D56D] print:border print:border-[#0d0a1f]/30 print:bg-transparent">
                      <Bot className="h-5 w-5" strokeWidth={2.2} />
                    </span>
                    <div>
                      <div className="text-2xl font-extrabold tabular-nums text-[#24D56D] md:text-3xl">
                        <CountUp value={shareOfWorkPct} />%
                      </div>
                      <div className="text-xs text-white/70 md:text-sm">{t.result.roiStripShareLabel}</div>
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.45, delay: 0.08 }}
                    className="flex items-center gap-4 rounded-xl border border-[#24D56D]/35 bg-[#24D56D]/[0.07] p-4 print:border-[#0d0a1f]/25 print:bg-transparent"
                  >
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#24D56D]/15 text-[#24D56D] print:border print:border-[#0d0a1f]/30 print:bg-transparent">
                      <Clock className="h-5 w-5" strokeWidth={2.2} />
                    </span>
                    <div>
                      <div className="text-2xl font-extrabold tabular-nums text-[#24D56D] md:text-3xl">
                        <CountUp value={annualHoursSaved} />
                      </div>
                      <div className="text-xs text-white/70 md:text-sm">{t.result.roiStripTimeLabel}</div>
                    </div>
                  </motion.div>
                </div>
                {/* Each metric becomes a measurable "target scorecard" - numbered,
                    icon-chipped, staggered in. Borders + tinted chips keep it
                    legible when saved as PDF (no reliance on dark fills). */}
                <div className="grid gap-3.5 sm:grid-cols-2">
                  {blueprint.successMetrics.map((m, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.4, delay: i * 0.07 }}
                      whileHover={{ y: -4 }}
                      className="group relative flex gap-3.5 rounded-xl border border-[#24D56D]/25 bg-[#24D56D]/[0.05] p-4 print:border-[#0d0a1f]/25 print:bg-transparent"
                    >
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#24D56D]/15 text-[#24D56D] transition group-hover:scale-110 print:border print:border-[#0d0a1f]/30 print:bg-transparent">
                        <Target className="h-5 w-5" strokeWidth={2.2} />
                      </span>
                      <div className="min-w-0">
                        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider tabular-nums text-[#24D56D]/80">
                          {t.result.successMetricTag} {String(i + 1).padStart(2, "0")}
                        </div>
                        <p className="text-sm font-medium leading-relaxed text-white/90 md:text-[15px]">{m}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </SectionShell>
            </>
          )}

          {/* 09 · Data needed */}
          {blueprint.dataNeeded && (
            <>
              <FlowArrow accent="neutral" dir={dir} />
              <SectionShell tone="neutral" step="09" icon={<Database className="h-5 w-5" />} title={t.result.dataNeededTitle}>
                <p className="whitespace-pre-line leading-relaxed text-white/90 md:text-lg">{blueprint.dataNeeded}</p>
              </SectionShell>
            </>
          )}

          {/* 10 · Next step */}
          <FlowArrow accent="green" label={t.result.flow.toNextStep} dir={dir} />
          <motion.div {...REVEAL} className="relative overflow-hidden rounded-2xl border border-[#24D56D]/40 bg-gradient-to-br from-[#24D56D]/[0.10] via-transparent to-[#988BFD]/[0.08] p-7 md:p-8">
            <div aria-hidden className="ab-print-hide pointer-events-none absolute -top-16 end-[-3rem] h-44 w-44 rounded-full bg-[#24D56D]/15 blur-3xl" />
            <div className="relative">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#24D56D]/40 bg-[#24D56D]/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#24D56D] print:bg-transparent">
                <Rocket className="h-3.5 w-3.5" /> {t.result.nextStep}
              </div>
              <p className="text-base leading-relaxed text-white md:text-lg">{blueprint.nextStep}</p>

              {/* Launch roadmap - turns the abstract "next step" into a concrete,
                  dated path. The middle node (the live pilot, named after the
                  top-impact agent) is emphasized as the moment it gets real.
                  Tags carry the time-order, so it reads correctly in both RTL
                  and LTR without directional arrows. */}
              {(() => {
                const topAgent = blueprint.agents[0]?.name ?? (lang === "he" ? "הסוכן הראשון" : "the first agent")
                const tl = t.result.nextStepTimeline
                const milestones = [
                  { icon: <Phone className="h-4 w-4" strokeWidth={2.2} />, tag: tl.step1.tag, title: tl.step1.title, desc: tl.step1.desc, hero: false },
                  { icon: <Bot className="h-4 w-4" strokeWidth={2.2} />, tag: tl.step2.tag, title: tl.step2.title, desc: tl.step2.desc(topAgent), hero: true },
                  { icon: <CheckCircle2 className="h-4 w-4" strokeWidth={2.2} />, tag: tl.step3.tag, title: tl.step3.title, desc: tl.step3.desc, hero: false },
                ]
                return (
                  <div className="mt-6 grid gap-3 sm:grid-cols-3">
                    {milestones.map((m, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.4, delay: i * 0.12 }}
                        className={`relative flex flex-col rounded-xl border p-4 ${
                          m.hero
                            ? "border-[#24D56D]/55 bg-[#24D56D]/[0.09] shadow-[0_0_28px_rgba(36,213,109,0.14)] print:shadow-none"
                            : "border-white/15 bg-white/[0.03]"
                        } print:bg-transparent`}
                      >
                        <div className="mb-2.5 flex items-center gap-2">
                          <span
                            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg print:border print:border-[#0d0a1f]/30 print:bg-transparent ${
                              m.hero ? "bg-[#24D56D] text-black" : "bg-white/10 text-white/80"
                            }`}
                          >
                            {m.icon}
                          </span>
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                              m.hero ? "bg-[#24D56D]/20 text-[#24D56D]" : "bg-white/10 text-white/65"
                            } print:bg-transparent`}
                          >
                            {m.tag}
                          </span>
                        </div>
                        <div className={`text-sm font-bold ${m.hero ? "text-[#24D56D]" : "text-white"}`}>{m.title}</div>
                        <p className="mt-1 text-xs leading-relaxed text-white/70">{m.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                )
              })()}

              <div className="ab-print-hide mt-6 flex justify-center">
                <motion.button
                  type="button"
                  onClick={scrollToBooking}
                  {...TAP}
                  className="rounded-full bg-[#24D56D] px-8 py-3.5 font-bold text-black shadow-[0_8px_28px_rgba(36,213,109,0.35)]"
                >
                  {t.result.bookCta}
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* CTA row - WhatsApp + email share */}
          <FlowArrow accent="green" dir={dir} />
          <motion.div {...REVEAL} className="ab-print-hide flex flex-col flex-wrap items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <motion.a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick({ page: 'ai_blueprint', location: 'result_cta_row', lang })}
              {...TAP}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-4 font-bold text-white shadow-[0_8px_28px_rgba(37,211,102,0.3)]"
            >
              <WhatsAppIcon className="h-5 w-5" />
              {t.result.chatCta}
            </motion.a>
            <motion.button
              type="button"
              onClick={() => {
                setShareState("idle")
                setShareDialogOpen(true)
              }}
              {...TAP}
              className="rounded-full border border-white/40 px-7 py-4 font-bold text-white/85 hover:border-white hover:text-white"
            >
              ✉ {t.result.emailReportCta}
            </motion.button>
          </motion.div>

          {/* Small print-as-PDF link, quieter - not a primary CTA */}
          <motion.div {...REVEAL} className="ab-print-hide flex justify-center">
            <button
              type="button"
              onClick={() => typeof window !== "undefined" && window.print()}
              className="text-xs text-white/55 underline-offset-4 transition hover:text-white hover:underline md:text-sm"
            >
              🖨 {t.result.downloadPdfCta}
            </button>
          </motion.div>

          {emailState === "sent" && contact && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="ab-print-hide flex justify-center"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-[#24D56D]/40 bg-[#24D56D]/10 px-4 py-2 text-xs text-[#24D56D] md:text-sm">
                {t.result.emailedTo(contact.email)}
              </div>
            </motion.div>
          )}

          {/* Footnote - segues from "an AI wrote this" into Why Palmidos */}
          <motion.p {...REVEAL} className="pt-2 text-center text-sm text-white/70">
            {t.result.footnote}
          </motion.p>

          {/* Why Palmidos - first/only brand-forward section */}
          {blueprint.whyPalmidos && (
            <>
              <FlowArrow accent="purple" dir={dir} />
              <motion.div {...REVEAL}>
                <WhyPalmidos
                  title={t.result.whyPalmidosTitle}
                  subtitle={blueprint.whyPalmidos}
                  isRtl={lang === "he"}
                  points={[
                    {
                      icon: "shield",
                      title: lang === "he" ? "מומחי סוכני AI" : "AI agent experts",
                      description: lang === "he"
                        ? "בנינו עשרות סוכני AI בייצור. יודעים בדיוק מה עובד בחברות אמיתיות."
                        : "We've shipped dozens of AI agents to production. We know what works for real businesses.",
                    },
                    {
                      icon: "dollar",
                      title: lang === "he" ? "מחיר סגור מראש" : "Fixed price upfront",
                      description: lang === "he"
                        ? "יודעים בדיוק כמה זה יעלה לפני שמתחילים. בלי חריגות, בלי הפתעות."
                        : "Know the exact cost before we start. No overruns, no surprises.",
                    },
                    {
                      icon: "rocket",
                      title: lang === "he" ? "פיילוט עובד תוך שבועיים" : "Working pilot in 2 weeks",
                      description: lang === "he"
                        ? "סוכן עובד תוך שבועיים - לא חצי שנה."
                        : "A live agent in 2 weeks - not six months.",
                    },
                  ]}
                />
              </motion.div>
            </>
          )}

          {/* arrow into the booking section below */}
          <FlowArrow accent="green" dir={dir} />
        </div>

        {/* Booking */}
        <section id="book-meeting" className="mt-8 scroll-mt-24">
          <motion.h2 {...REVEAL} className="mb-6 text-center text-2xl font-extrabold md:text-3xl">{t.result.bookHere}</motion.h2>
          {/* Hide the booking-form "message" textarea - the AI Blueprint already
              captured the prospect's pain in detail; asking again ("מה האתגר?")
              is redundant. The hiding CSS lives in the global <style jsx>
              block at the top of this component (search "ab-book-here"). */}
          <div className="ab-book-here">
            <BookingCalendar
              dict={{
                ...calendarDict,
                consultationTitle: lang === "he"
                  ? "שיחה על בניית סוכני ה-AI שלי"
                  : "Call about building my AI agents",
              }}
              initialSubject={BookingSubject.CONSULTATION}
              embedded
              lang={lang}
              leadData={contact ? {
                firstName: contact.name,
                email: contact.email,
                phone: contact.phone,
              } : undefined}
              // landingPageSlug routes the booking through /api/landing-page/book
              // (which uses the Pal AI agent for WhatsApp + manager alert + email),
              // instead of the legacy /api/calendar path whose notifications are
              // wired to the now-disabled website WhatsApp service.
              landingPageSlug="ai-blueprint"
            />
          </div>
          <div className="ab-print-hide mt-6 flex justify-center">
            <motion.a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick({ page: 'ai_blueprint', location: 'form_section', lang })}
              {...TAP}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-4 font-bold text-white shadow-[0_8px_28px_rgba(37,211,102,0.3)]"
            >
              <WhatsAppIcon className="h-5 w-5" />
              {t.result.chatCta}
            </motion.a>
          </div>
        </section>

        {/* Sticky mobile CTA - slides up after the report lands */}
        <motion.div
          initial={{ y: 90 }}
          animate={{ y: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 28, delay: 0.5 }}
          className="ab-print-hide fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-white/10 bg-[#0d0a1f]/95 p-3 backdrop-blur sm:hidden"
        >
          <motion.button
            type="button"
            onClick={scrollToBooking}
            whileTap={{ scale: 0.96 }}
            className="flex-1 rounded-full bg-[#24D56D] py-3 text-center text-sm font-bold text-black"
          >
            {t.result.bookCta}
          </motion.button>
          <motion.a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick({ page: 'ai_blueprint', location: 'mobile_sticky_cta', lang })}
            whileTap={{ scale: 0.94 }}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-bold text-white"
          >
            <WhatsAppIcon className="h-5 w-5" />
          </motion.a>
        </motion.div>

        {/* Share dialog - opens when the prospect clicks "✉ Email me this report".
            Confirms whether the auto-send already reached their inbox and
            offers an input for forwarding to additional people. When the
            auto-send FAILED (SendGrid rejected, etc.), the dialog tells the
            user honestly and offers a one-click retry to their own address. */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent dir={dir} className="bg-[#1a1530] text-white">
            <DialogHeader>
              <DialogTitle>{t.result.shareDialog.title}</DialogTitle>
              <DialogDescription className="text-white/70">
                {contact && emailState === "sent"
                  ? t.result.shareDialog.alreadySentTo(contact.email)
                  : t.result.shareDialog.notYetSent}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              {emailState !== "sent" && contact && (
                <button
                  type="button"
                  disabled={emailState === "sending"}
                  onClick={() => blueprint && sendEmailReport(contact, blueprint)}
                  className="rounded-full border border-[#24D56D]/60 bg-[#24D56D]/10 py-3 text-sm font-bold text-[#24D56D] transition hover:bg-[#24D56D]/20 disabled:opacity-50"
                >
                  {emailState === "sending"
                    ? t.result.shareDialog.sending
                    : t.result.shareDialog.sendToMeCta(contact.email)}
                </button>
              )}
              <label className="text-xs text-white/65">{t.result.shareDialog.hint}</label>
              <input
                value={extraEmails}
                onChange={(e) => setExtraEmails(e.target.value)}
                placeholder={t.result.shareDialog.placeholder}
                className="rounded-xl border border-white/30 bg-white/10 px-4 py-3 outline-none focus:border-[#24D56D]"
                disabled={shareState === "sending"}
              />
              {shareState === "error" && (
                <div className="text-xs text-red-400">{t.result.shareDialog.errorMsg}</div>
              )}
              {shareState === "sent" && (
                <div className="text-xs text-[#24D56D]">{t.result.shareDialog.sentExtra}</div>
              )}
              <button
                type="button"
                disabled={shareState === "sending" || extraEmails.trim().length === 0}
                onClick={sendShareEmails}
                className="mt-1 rounded-full bg-[#24D56D] py-3.5 font-bold text-black transition hover:bg-[#24D56D]/90 disabled:opacity-50"
              >
                {shareState === "sending" ? t.result.shareDialog.sending : t.result.shareDialog.sendCta}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  // ── Wizard + contact dialog ──
  // (The landing page at /[lang]/ai-blueprint is hero-only; the wizard lives
  // on its own route /[lang]/ai-blueprint/start, so we render immediately.)

  const painLongEnough = painText.trim().length >= PAIN_MIN_CHARS
  const setupLongEnough = currentSetup.trim().length >= SETUP_MIN_CHARS
  const canNext =
    (step === 1 && (isIndustryOther ? industryOther.trim().length > 0 : !!industry)) ||
    (step === 2 && !!customerType) ||
    (step === 3 && painLongEnough) ||
    (step === 4 && !!monthlyHours) ||
    (step === 5 && setupLongEnough)

  return (
    <>
      {showLoader ? (
        <LoadingScreen dir={dir} steps={t.loadingMessages} activeStep={stepIdx} />
      ) : (
        <div
          dir={dir}
          className="mx-auto flex w-full max-w-xl flex-col px-3 pt-24 pb-6 text-white sm:px-4 sm:pt-32"
          style={{ minHeight: "100dvh" }}
        >
          <div className="flex-none">
            <Progress value={(step / TOTAL_STEPS) * 100} className="bg-white/20" />
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-red-400 bg-red-500/10 p-4 text-center text-sm">
              {t.error}
              <button className="ms-2 underline" onClick={() => { setError(false); setDialogOpen(true) }}>{t.retry}</button>
            </div>
          )}

          {/* Step content lives in a flex-1 panel that scrolls INTERNALLY when
              it overflows (most relevant on step 3: textarea + ~13 chips).
              Pinning the progress at the top and the nav row at the bottom
              keeps Next reachable without page-level scroll. */}
          <div className="my-6 flex min-h-0 flex-1 flex-col">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: dir === "rtl" ? -24 : 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir === "rtl" ? 24 : -24 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="flex min-h-0 flex-1 flex-col overflow-y-auto pe-1"
              >
              {step === 1 && (
                <>
                  <h2 className="mb-6 text-2xl font-bold">{t.headings.step1}</h2>
                  <motion.div
                    variants={STAGGER}
                    initial="hidden"
                    animate="visible"
                    className="flex flex-wrap gap-2.5"
                  >
                    {t.industries.map((o) => (
                      <Chip
                        key={o}
                        active={industry === o}
                        onClick={() => {
                          setIndustry(o)
                          if (o !== t.otherKey) setStep(2)
                        }}
                      >
                        {o}
                      </Chip>
                    ))}
                  </motion.div>
                  {isIndustryOther && (
                    <input
                      value={industryOther}
                      onChange={(e) => setIndustryOther(e.target.value)}
                      placeholder={t.otherPlaceholder}
                      className="mt-4 w-full rounded-xl border border-white/40 bg-white/10 px-4 py-3 text-white placeholder:text-white/50 outline-none focus:border-[#24D56D]"
                      autoFocus
                    />
                  )}
                </>
              )}

              {step === 2 && (
                <>
                  <h2 className="mb-6 text-2xl font-bold">{t.headings.step2}</h2>
                  <motion.div variants={STAGGER} initial="hidden" animate="visible" className="grid gap-3 sm:grid-cols-3">
                    {t.customerTypes.map((c) => (
                      <motion.button
                        key={c.key}
                        type="button"
                        variants={ITEM}
                        whileHover={{ y: -3 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => { setCustomerType(c.key); setStep(3) }}
                        className={`rounded-2xl border px-4 py-5 text-center text-white transition-colors ${
                          customerType === c.key ? "border-[#24D56D] bg-[#24D56D]/15" : "border-white/40 hover:border-white"
                        }`}
                      >
                        <div className="text-xl font-bold">{c.label}</div>
                        <div className="mt-1 text-xs text-white/65">{c.hint}</div>
                      </motion.button>
                    ))}
                  </motion.div>
                </>
              )}

              {step === 3 && (
                <>
                  <h2 className="mb-3 text-2xl font-bold">{t.headings.step3}</h2>
                  <textarea
                    rows={5}
                    value={painText}
                    onChange={(e) => setPainText(e.target.value)}
                    placeholder={t.headings.step3Placeholder}
                    className="mb-2 w-full resize-none rounded-xl border border-white/40 bg-white/10 px-4 py-3 text-base leading-relaxed text-white placeholder:text-white/50 outline-none focus:border-[#24D56D]"
                  />
                  <div className="mb-6 text-xs text-white/55">{t.headings.step3MinHint}</div>
                  <div className="mb-3 text-sm text-white/60">{t.headings.step3ChipsHint}</div>
                  <motion.div variants={STAGGER} initial="hidden" animate="visible" className="flex flex-wrap gap-2">
                    {t.processChips.map((o) => (
                      <Chip key={o} active={painChips.includes(o)} onClick={() => togglePainChip(o)}>{o}</Chip>
                    ))}
                  </motion.div>
                </>
              )}

              {step === 4 && (
                <>
                  <h2 className="mb-6 text-2xl font-bold">{t.headings.step4}</h2>
                  <motion.div variants={STAGGER} initial="hidden" animate="visible" className="flex flex-wrap gap-2.5">
                    {t.monthlyHoursOptions.map((o) => (
                      <Chip key={o} active={monthlyHours === o} onClick={() => { setMonthlyHours(o); setStep(5) }}>{o}</Chip>
                    ))}
                  </motion.div>
                </>
              )}

              {step === 5 && (
                <>
                  <h2 className="mb-3 text-2xl font-bold">{t.headings.step5}</h2>
                  <textarea
                    rows={5}
                    value={currentSetup}
                    onChange={(e) => setCurrentSetup(e.target.value)}
                    placeholder={t.headings.step5Placeholder}
                    className="mb-2 w-full resize-none rounded-xl border border-white/40 bg-white/10 px-4 py-3 text-base leading-relaxed text-white placeholder:text-white/50 outline-none focus:border-[#24D56D]"
                  />
                  <div className="text-xs text-white/55">{t.headings.step5OptionalHint}</div>
                </>
              )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex-none flex items-center justify-between">
            <button
              type="button"
              disabled={step === 1}
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="-mx-2 rounded-lg px-3 py-2.5 text-base text-white/75 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-white/75 sm:text-sm"
            >
              {t.buttons.back}
            </button>
            {step === TOTAL_STEPS ? (
              <motion.button
                type="button"
                onClick={openContactDialog}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.96 }}
                className="rounded-full bg-[#24D56D] px-8 py-4 text-base font-bold text-black shadow-[0_8px_28px_rgba(36,213,109,0.32)]"
              >
                {t.buttons.submit}
              </motion.button>
            ) : (
              <motion.button
                type="button"
                disabled={!canNext}
                onClick={() => setStep((s) => Math.min(TOTAL_STEPS, s + 1))}
                whileTap={{ scale: canNext ? 0.96 : 1 }}
                className="rounded-full bg-white/10 px-8 py-4 text-base font-bold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white/10"
              >
                {t.buttons.next}
              </motion.button>
            )}
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent dir={dir} className="bg-[#1a1530] text-white">
          <DialogHeader>
            <DialogTitle>{t.dialog.title}</DialogTitle>
            <DialogDescription className="text-white/70">{t.dialog.description}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onContactSubmit)} className="flex flex-col gap-3">
            <input {...register("name")} placeholder={t.contactFields.name} className="rounded-xl border border-white/30 bg-white/10 px-4 py-3 outline-none focus:border-[#24D56D]" />
            {errors.name && <span className="text-xs text-red-400">{errors.name.message}</span>}
            <input {...register("email")} placeholder={t.contactFields.email} className="rounded-xl border border-white/30 bg-white/10 px-4 py-3 outline-none focus:border-[#24D56D]" />
            {errors.email && <span className="text-xs text-red-400">{errors.email.message}</span>}
            <input {...register("phone")} placeholder={t.contactFields.phone} className="rounded-xl border border-white/30 bg-white/10 px-4 py-3 outline-none focus:border-[#24D56D]" />
            {errors.phone && <span className="text-xs text-red-400">{errors.phone.message}</span>}
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="mt-2 rounded-full bg-[#24D56D] py-4 font-bold text-black"
            >
              {t.buttons.finalize}
            </motion.button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Chip({ active, onClick, children }: { active?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <motion.button
      type="button"
      variants={ITEM}
      whileTap={{ scale: 0.94 }}
      onClick={onClick}
      className={`rounded-full border px-5 py-3 text-base text-white transition-colors ${
        active ? "border-[#24D56D] bg-[#24D56D]/15" : "border-white/40 hover:border-white"
      }`}
    >
      {children}
    </motion.button>
  )
}
